import cookie from 'cookie-cutter'
import logger from '../logger'
import appProps from '../app-props.js'
import ItemUtils from '#util/utils/item-utils'
import TextUtils from '#util/utils/text-utils'
import PriceUtils from '#util/utils/price-utils'
import AddressUtils from '#util/utils/address-utils'
import FulfillmentUtils from '#util/utils/fulfillment-utils'
import CouponUtils from '#util/utils/coupon-utils'
import UserUtils from '#util/utils/user-utils'
import { OrderIssues } from '#util/utils/error-utils'
import defaultShippingIDs from '../maps/defaultShippingIDs'
import SKU_SHIPPING_RESTRICTIONS from '../maps/skuShippingRestrictions'
import {
  get,
  filter,
  reduce,
  intersectionWith,
  find,
  merge,
  uniqBy,
  noop,
  differenceWith,
  defaultTo,
  isNil,
  isPlainObject,
  isEmpty,
  isNumber,
  uniqWith,
  concat,
  map,
} from 'lodash-es'


// ////////////////
// Order Getters //
// ////////////////

// These getters are designed to keep the logic needed for special properties
// not available on the order as a whole, in one place. By breaking these functions out
// and by creating a global mixin that automatically makes every component a
// subscriber of the order model (as that component is mounted) we are guaranteed a
// single source of truth for this information. Due to the fact that these getters
// are used in several places across the application and that they all need to stay
// in-sync, it makes sense to make them centrally located for ease of maintenance. It also
// eliminates the guesswork of "where do I find that property?". Instead of making special
// getters children of sub-models, they will always live at the orderModel root.

export default {
  taxTotal: {
    get: function getTaxTotal() {
      logger('taxes.total', 'debug')()
      return get(this.model, 'taxes.total', null)
    },
    enumerable: true,
  },

  shipFullFirstName: {
    get: function getShipFullFirstName() {
      logger('shipFullFirstName', 'debug')()
      const shipItems = this.model.safeGet('getters.filteredShipLineItems')
      const giftItems = this.model.safeGet('getters.filteredPhysicalGiftLineItems')

      const shipPrefix = shipItems.length ? get(shipItems[0], 'shippingAddress.address.prefix') : get(giftItems[0], 'shippingAddress.address.prefix')
      const shipFirstName = shipItems.length ? get(shipItems[0], 'shippingAddress.address.firstName') : get(giftItems[0], 'shippingAddress.address.firstName')
      const shipFullFirstName = shipPrefix ? `${shipPrefix} ${shipFirstName}` : shipFirstName

      return shipFullFirstName
    },
    enumerable: true,
  },

  shipFullLastName: {
    get: function getShipFullLastName() {
      logger('shipFullLastName', 'debug')()
      const shipItems = this.model.safeGet('getters.filteredShipLineItems')
      const giftItems = this.model.safeGet('getters.filteredPhysicalGiftLineItems')

      const shipSuffix = shipItems.length ? get(shipItems[0], 'shippingAddress.address.suffix') : get(giftItems[0], 'shippingAddress.address.suffix')
      const shipLastName = shipItems.length ? get(shipItems[0], 'shippingAddress.address.lastName') : get(giftItems[0], 'shippingAddress.address.lastName')
      const shipFullLastName = shipSuffix ? `${shipLastName} ${shipSuffix}` : shipLastName

      return shipFullLastName
    },
    enumerable: true,
  },

  shipState: {
    get: function getShipState() {
      const item = this.model.safeGet('getters.filteredShipLineItems').length ? this.model.safeGet('getters.filteredShipLineItems[0]') : this.model.safeGet('getters.filteredPhysicalGiftLineItems[0]')
      const countryCode = get(item, 'shippingAddress.address.countryCode') || this.model.safeGet('billingAddress.address.countryCode')
      const shipState = get(item, 'shippingAddress.address.stateCode')
      const billingState = this.model.safeGet('billingAddress.address.stateCode')

      // If the address is US or CA and there is no state default to the billing state.
      // If it's an international, non-CA, address then don't use the billing state
      return countryCode === 'US' || countryCode === 'CA' ? shipState || billingState : shipState
    },
    enumerable: true,
  },

  currentBillingAddress: {
    get: function getCurrentBillingAddress() {
      const address =
        this.model.safeGet('billingAddress.address', undefined) ||
        (!this.model.safeGet('user.isGuest')
          ? this.model.safeGet('user.savedBillingAddress.address', undefined)
          : undefined)
      return address || AddressUtils.createDefault()
    },
  },

  hasCompleteContactAndBillingInfo: {
    get: function getHasCompleteContactAndBillingInfo() {
      const address = this.model.safeGet('billingAddress.address', undefined)
      // ||
      // (!this.model.safeGet('user.isGuest')
      //   ? this.model.safeGet('user.savedBillingAddress.address', undefined)
      //   : undefined);

      const requiredFields = [
        'firstName',
        'lastName',
        'city',
        'stateCode',
        'countryCode',
        'postalCode',
        'line1',
        'stateCode,',
        'phoneNumber',
        'email',
      ]

      // make sure the address contains the required fields
      // and that they are not null or undefined
      return requiredFields.some(value => get(address, value) != null)
    },
  },

  billFullFirstName: {
    get: function getBillFullFirstName() {
      logger('billFullFirstName', 'debug')()

      const billPrefix = this.model.safeGet('billingAddress.address.prefix')
      const billFirstName = this.model.safeGet('billingAddress.address.firstName')
      const billFullFirstName = billPrefix ? `${billPrefix} ${billFirstName}` : billFirstName

      return billFullFirstName
    },
    enumerable: true,
  },

  billFullLastName: {
    get: function getBillFullLastName() {
      logger('billFullLastName', 'debug')()

      const billSuffix = this.model.safeGet('billingAddress.address.suffix')
      const billLastName = this.model.safeGet('billingAddress.address.lastName')
      const billFullLastName = billSuffix ? `${billLastName} ${billSuffix}` : billLastName

      return billFullLastName
    },
    enumerable: true,
  },

  hasNonDividendableItems: {
    get: function getHasNonDividendableItems() {
      let hasNonDividendable = false
      const lineItems = this.model.safeGet('lineItems', [])

      lineItems.forEach((item) => {
        if (!item.dividenable && !ItemUtils.isPromoGiftCard(item)) {
          hasNonDividendable = true
        }
      })

      return hasNonDividendable
    },
    enumerable: true,
  },

  filteredShipLineItems: {
    get: function getFilteredShipLineItems() {
      logger('filtered Ship Items', 'debug')()

      // If the item has a fulfillmentType of 'SHIP' then it is ship
      // if there is no address set, then it is ship
      // if there is an address set but no storeId then it is ship
      // NOTE: Filtering out giftcards, hidden items and memberships!
      const filtered = get(this.model, 'lineItems', []).filter((lineItem) => {
        if (get(lineItem, 'giftCardDetails') || ItemUtils.isMembership(lineItem) || get(lineItem, 'hidden')) return false
        else if (get(lineItem, 'shipMethod.fulfillmentType') === 'SHIP'
          || get(lineItem, 'shippingAddress') === null
          || (get(lineItem, 'shippingAddress') && get(lineItem, 'shippingAddress.address.storeId') == null)
        ) return true

        // default for no match
        return false
      })

      return filtered
    },
    enumerable: true,
  },

  filteredShipLineItemsWithGC: {
    get: function getFilteredShipLineItemsWithGC() {
      logger('filtered Ship Items', 'debug')()

      // If the item has a fulfillmentType of 'SHIP' then it is ship
      // if there is no address set, then it is ship
      // if there is an address set but no storeId then it is ship
      // NOTE: Filtering out memberships!
      const filtered = get(this.model, 'lineItems', []).filter((lineItem) => {
        if (ItemUtils.isMembership(lineItem)) return false
        else if (ItemUtils.isDigitalGiftCard(lineItem)) return false
        else if (ItemUtils.isPromoGiftCard(lineItem)) return false
        else if (get(lineItem, 'shipMethod.fulfillmentType') === 'SHIP'
          || get(lineItem, 'shippingAddress') === null
          || (get(lineItem, 'shippingAddress') && get(lineItem, 'shippingAddress.address.storeId') == null)
        ) return true

        // default for no match
        return false
      })

      return filtered
    },
    enumerable: true,
  },

  filteredGiftCards: {
    get: function filteredGCs() {
      return get(this.model, 'lineItems', []).filter((lineItem) => ItemUtils.isGiftCard(lineItem))
    },
    enumerable: true,
  },

  filteredDigitalGiftLineItems: {
    get: function getFilteredDigitalGiftLineItems() {
      logger('filtered Digital Gift Card Items', 'debug')()

      return this.model.safeGet('lineItems', []).filter(lineItem => ItemUtils.isDigitalGiftCard(lineItem))
    },
    enumerable: true,
  },

  filteredPromoGiftLineItems: {
    get: function getFilteredPromoGiftLineItems() {
      logger('filtered Promo Gift Card Items', 'debug')()

      return this.model.safeGet('lineItems', []).filter(lineItem => ItemUtils.isPromoGiftCard(lineItem))
    },
    enumerable: true,
  },

  filteredPhysicalGiftLineItems: {
    get: function getFilteredPhysicalGiftLineItems() {
      logger('filtered Physical Gift Card Items', 'debug')()

      return this.model.safeGet('lineItems', []).filter(lineItem => ItemUtils.isPhysicalGiftCard(lineItem))
    },
    enumerable: true,
  },

  filteredPickupLineItems: {
    get: function getFilteredPickupLineItems() {
      logger('filtered Pickup Items', 'debug')()

      // NOTE: this is a patch for now. The API requires that other clients set not
      // only the address for the item being shipped/picked-up but also a separate
      // part of the object with the shipMethod.id for item. This is then
      // used later in the audit process to verify the selection. This was unexpected
      // so the clients are not currently setting this information. Eventually changes
      // should make their way into production up stream which will allow the filter
      // logic to be more straight-forward

      // If the item has a fulfillmentType of 'PICKUP' then it is pickup
      // if there is a storeId set for the address, then it is pickup.
      // NOTE: filtering out pickup items that are hidden
      return get(this.model, 'lineItems', []).filter(o => !get(o, 'hidden') && (get(o, 'shipMethod.fulfillmentType') === 'PICKUP'
        || get(o, 'shippingAddress.address.storeId') != null))
    },
    enumerable: true,
  },

  filteredMembershipCardItems: {
    get: function getFilteredMemebershipItems() {
      logger('get filtered membership items', 'debug')()
      return filter(get(this.model, 'lineItems', []), { sku: '6485920018' })
    },
    enumerable: true,
  },

  hasNewMembership: {
    get: function hasNewMembership() {
      return get(this.model, 'getters.filteredMembershipCardItems', []).length > 0
    },
    enumerable: true,
  },

  isMemberOrNewMember: {
    get: function isMemberOrNewMember() {
      return !!(UserUtils.isMember(this.model.customerInfo) || get(this.model, 'getters.hasNewMembership', false))
    },
  },

  // Items for which we want to fetch fulfillment via FDD
  filteredFulfillmentItems: {
    get: function getFilteredFulfillmentItems() {
      logger('get filtered fulfillment items', 'debug')()
      return get(this.model, 'lineItems', []).filter((lineItem) => {
        if (
          ItemUtils.isMembership(lineItem)
          || ItemUtils.isPromoGiftCard(lineItem)
          || ItemUtils.isMABC(lineItem)
          || ItemUtils.isDigitalGiftCard(lineItem)
          || get(lineItem, 'hidden')
        ) {
          return false
        }
        // default for no match
        return true
      })
    },
    enumerable: true,
  },

  filteredCommonShipMethods: {
    // eslint-disable-next-line
    get: function getFilteredCommonShipMethods() {
      logger('filtered common ship methods', 'debug')()
      const useByEads = appProps.get('checkout.audit.use.by.ead', false)
      const commonShipMethods = []
      const reducedGiftCards = []
      const filteredShipLineItems = this.model.getters.filteredShipLineItems
      let reduced = reduce(filteredShipLineItems, (result, value) => {
        if (this.fulfillmentsModel && this.fulfillmentsModel[value.reiRefId] !== undefined) {
          const shipInfo = this.fulfillmentsModel[value.reiRefId].fulfillments[0].shipMethodsInformation
          const methodIds = shipInfo.allowedShipMethods

          if (get(methodIds, '0.id', '') === 'GIFT_CARD_AND_MEMBERSHIP') {
            reducedGiftCards.push(methodIds)
          } else if (!FulfillmentUtils.isUnavailable(shipInfo.restrictions)) result.push(methodIds)
        }
        return result
      }, [])

      reduced = !reduced.length ? reducedGiftCards : reduced
      const comparator = (arrVal, othVal) => arrVal.id === othVal.id
      reduced.push(comparator)
      const common = intersectionWith.apply(null, reduced)
      // Now we need to take the common ship methods we have filtered and apply all the fulillments
      // for each line item and each ship method
      common.forEach((shipMethod) => {
        const commonMethod = {
          id: shipMethod.id,
          description: shipMethod.description,
          fulfillmentInfos: [],
          ...(useByEads && {
            oversizeShippingCharge: shipMethod.oversizeShippingCharge,
          }),
        }

        let charge = 0
        let discount = 0

        filteredShipLineItems.forEach((lineItem) => {
          const fulfillments = find(
            get(lineItem, 'fulfillments.allowedShipMethods', []),
            method => method.id === shipMethod.id
          )

          charge += get(fulfillments, 'shippingCharge.charge', 0)
          discount += get(fulfillments, 'shippingCharge.discount', 0)

          const fulfillmentInfos = fulfillments ? fulfillments.fulfillmentInfos : []
          commonMethod.fulfillmentInfos =
            shipMethod.fulfillmentInfos.concat(fulfillmentInfos)
        })

        if (useByEads) {
          commonMethod.shippingCharge = {
            charge: PriceUtils.roundTo(charge),
            discount: PriceUtils.roundTo(discount),
          }
        }

        commonShipMethods.push(commonMethod)
      })

      return commonShipMethods
    },
    enumerable: true,
    configurable: false,
  },

  filteredPhysicalGiftShipMethods: {
    get: function getFilteredPhysicalGiftShipMethods() {
      const physicalGiftItems = this.model.safeGet('getters.filteredPhysicalGiftLineItems', []).map(item =>
        // add the selected fulfillment to the item
        merge(item, {
          fulfillments: get(this.model.getters.fulfillmentsModel, `${item.reiRefId}.fulfillments['0'].shipMethodsInformation`, []),
        }))
      return (physicalGiftItems && physicalGiftItems.length > 0)
        ? physicalGiftItems[0].fulfillments.allowedShipMethods
        : []
    },
    enumerable: true,
  },

  filteredShipGroups: {
    get: function getFilteredShipGroups() {
      logger('filtered Ship Groups', 'debug')()
      return get(this.shipChargesModel, 'shippingGroups', []).filter(o => get(o, 'category') === 'MERCHANDISE')
    },
    enumerable: true,
    configurable: false,
  },

  filteredPickupGroups: {
    get: function getFilteredPickupGroups() {
      logger('filtered Ship Groups', 'debug')()
      const useByEads = appProps.get('checkout.audit.use.by.ead', false)


      if (!useByEads) {
        const stores = this.model.safeGet('getters.filteredPickupLineItems', [])
          .map(item => item.shippingAddress.address.storeId)
        const pickupGroups = get(this.shipChargesModel, 'shippingGroups', [])
          .filter(o => get(o, 'category') === 'STORE_PICKUP')

        // We want to check the pickupGroups against the current pickup line items so if a line item
        // has been removed because it's unavailable, or someother reason, we don't want to show
        // that pick up group
        return pickupGroups.filter((group) => {
          let hasItems = false

          stores.forEach((storeId) => {
            if (storeId === parseInt(group.shipTo.storeId, 10)) hasItems = true
          })
          return hasItems
        })
      }

      const pickupItems = this.model.safeGet('getters.filteredPickupLineItems', [])
      const stores = uniqBy(pickupItems, item => item.shippingAddress.address.storeId)
      return stores.map(item => ({ shipTo: item.shippingAddress.address }))
    },
    enumerable: true,
    configurable: false,
  },

  activeShipMethod: {
    value: function getActiveShipMethod(context) {
      logger('Active selected ship method OR default', 'debug')()
      // fetch the set method for the line items OR give the default
      // for that item's type/ship-to location OR of 'STANDARD'

      const shipItem = get(context, '[0]')
      const defaultShipMethodForItem = get(this, 'getters.determineShipDefaultByItem', noop).call(this, shipItem)
      const defaultShipMethod = defaultShipMethodForItem ? defaultShippingIDs[defaultShipMethodForItem] : 'STANDARD'
      const alreadySetMethod = get(context, '[0].shipMethod.id')

      // if the set method on the item is one that is actually available for that item,
      // this can happen if the item address changes after setting it.
      const finalCheck = get(shipItem, 'fulfillments.allowedShipMethods', []).some(method => method.id === alreadySetMethod) ? alreadySetMethod : defaultShipMethod

      return finalCheck
    },
    enumerable: true,
    configurable: false,
  },

  hasFulfillmentRestrictions: {
    get: function hasFulfillmentRestrictions() {
      const orderIssues = get(this.model, 'orderIssues', [])

      if (orderIssues.length) {
        const issues = new OrderIssues(orderIssues)

        if (issues.hasFulfillmentRestrictions()) {
          return true
        }
      }

      return false
    },
    enumerable: true,
  },

  isDividendCoveringBalance: {
    get: function isDividendCoveringBalance() {
      if (get(this.model, 'tenders', []) === null) return false
      const divArr = get(this.model, 'tenders', []).filter(o => get(o, 'type') === 'DIVIDEND')
      if (divArr.length === 0) return false
      let returnBool
      if (divArr[0].balance >= get(this.model, 'total')) {
        returnBool = true
      } else {
        returnBool = false
      }
      return returnBool
    },
    enumerable: true,
  },

  dividendEarned: {
    get: function dividendEarned() {
      const threshold = appProps.get('showDividendThreshold', 2)
      const amount = this.model.safeGet('estimatedDividendEarned', 0)
      return (amount > threshold)
        ? `${PriceUtils.format(amount)} (10%)`
        : '10%'
    },
    enumerable: true,
  },

  filteredGiftCardTenders: {
    get: function getFilteredGiftCardTenders() {
      logger('filtered Gift Card Tenders', 'debug')()
      if (get(this.model, 'tenders', []) === null) return []
      return get(this.model, 'tenders', []).filter(o =>
        ItemUtils.isPhysicalGiftCard(o)
        || ItemUtils.isDigitalGiftCard(o)
        || ItemUtils.isPromoGiftCard(o))
    },
    enumerable: true,
  },

  filteredGiftBonusCardTendersNoSaved: {
    get: function getFilteredGiftBonusCardTendersNoSaved() {
      logger('filtered Gift Card Tenders w/o saved', 'debug')()
      if (get(this.model, 'tenders', []) === null) return []

      const tenders = get(this.model, 'tenders', []).filter(o =>
        ItemUtils.isPhysicalGiftCard(o)
        || ItemUtils.isDigitalGiftCard(o)
        || ItemUtils.isPromoGiftCard(o))

      const saved = this.model.safeGet('getters.savedGiftCardsModel', {}).giftCards || []

      return differenceWith(tenders, saved, (arrVal, othVal) => arrVal.number === othVal.number)
    },
    enumerable: true,
  },

  filteredGiftCardTendersNoSaved: {
    get: function getFilteredGiftCardTendersNoSaved() {
      logger('filtered Gift Card Tenders w/o saved', 'debug')()
      if (get(this.model, 'tenders', []) === null) return []

      const tenders = get(this.model, 'tenders', []).filter(o =>
        ItemUtils.isPhysicalGiftCard(o)
        || ItemUtils.isDigitalGiftCard(o))

      const saved = this.model.safeGet('getters.savedGiftCardsModel', {}).giftCards || []
      return differenceWith(tenders, saved, (arrVal, othVal) => arrVal.number === othVal.number)
    },
    enumerable: true,
  },

  filteredBonusCardTendersNoSaved: {
    get: function getFilteredBonusCardTendersNoSaved() {
      logger('filtered Gift Card Tenders w/o saved', 'debug')()
      if (get(this.model, 'tenders', []) === null) return []

      const tenders = get(this.model, 'tenders', []).filter(o => ItemUtils.isPromoGiftCard(o))
      const saved = this.model.safeGet('getters.savedGiftCardsModel', {}).giftCards || []
      return differenceWith(tenders, saved, (arrVal, othVal) => arrVal.number === othVal.number)
    },
    enumerable: true,
  },

  // Checks to see if the order contains gift cards. If so, restrict
  // allowed tenders.
  // Exception: if there is only a MABC gift card in the order, allow purchase
  allowGiftCardTenderOnOrder: {
    get: function allowGiftCardTenderOnOrder() {
      const giftCardsInOrder = this.model.safeGet('getters.filteredGiftCards', []) || []
      const promoCardsInOrder = this.model.safeGet('getters.filteredPromoGiftLineItems', []) || []

      // If the number of gift cards in total is greater than
      // the number of promo cards, then the order must contain
      // at least 1 or more physical or electronic gift cards.
      // In this case, the user will not be allowed to add a
      // gift card as a tender.
      return !(giftCardsInOrder.length > promoCardsInOrder.length)
    },
    enumerable: true,
  },

  filteredAppliedSavedGCTenders: {
    get: function filteredAppliedSavedGCTenders() {
      const savedGC = this.model.safeGet('getters.savedGiftCardsModel', {}).giftCards || []
      const appliedGCTenders = this.model.safeGet('getters.filteredGiftCardTenders')

      // Both GC and BC
      // Get the delta between saved and applied cards (used to indicate "applied" state on the cards)
      const applied = intersectionWith(appliedGCTenders, savedGC, (arrVal, othVal) => arrVal.number === othVal.number)

      return applied
    },
    enumerable: true,
  },

  filteredValidSavedGCTenders: {
    get: function filteredValidSavedGCTenders() {
      const savedGC = this.model.safeGet('getters.savedGiftCardsModel', {}).giftCards || []

      // Both GC and BC
      // Get only the valid saved cards
      const valid = savedGC.filter(card => ItemUtils.isActiveGCorBC(card) && ItemUtils.hasBalanceGCorBC(card))

      return valid
    },
    enumerable: true,
  },


  /**
   * Determine the appropriate type of an item so that the correct
   * default shipping method can be derived.
   * return types include:
   * - isDefault
   * - isGiftCard
   * - isMembership
   * - isCanadian
   * - isInternational
   *
   * These correspond to a mapping in
   * `order-processing-web/src/main/js/util/maps/defaultShippingIDs.js`
   * @Param {Object} The item object being evaluated
   * @Returns {String} string identifier for KEY in map file
   */
  determineShipDefaultByItem: {
    value: function determineShipDefaultByItem(item) {
      logger('Determining default ship method for item', 'debug')()

      let defaultType = 'isDefault'

      // NOTE: `this` context is the model

      // if it is a physical giftcard or membership
      if (ItemUtils.isPhysicalGiftCard(item)) {
        defaultType = 'isGiftCard'
      } else if (ItemUtils.isMembership(item)) {
        defaultType = 'isMembership'
      } else if (get(item, 'shippingAddress')) { // Check to see if the item has a shipping address
        const itemCountryCode = get(item, 'shippingAddress.address.countryCode')
        if (itemCountryCode === 'US') {
          // If the item is domestic
          defaultType = 'isDefault'
        } else if (itemCountryCode === 'CA') {
          // If the item is canadian
          defaultType = 'isCanadian'
        } else {
          // If the item is international
          defaultType = 'isInternational'
        }
      } else if (this.safeGet('billingAddress.address')) { // Check to see if the order has a billing address
        const billingCountryCode = this.safeGet('billingAddress.address.countryCode')
        if (billingCountryCode === 'US') {
          // If the item is domestic
          defaultType = 'isDefault'
        } else if (billingCountryCode === 'CA') {
          // If the item is canadian
          defaultType = 'isCanadian'
        } else {
          // If the item is international
          defaultType = 'isInternational'
        }
      }

      return defaultType
    },
    enumerable: true,
    configurable: false,
  },

  // Getter for model of the API data from the fulfillments service, not part of the order
  fulfillmentsModel: {
    get: function getFulfillmentsModel() {
      logger('Fulfillments Model', 'debug')()
      return get(this, 'fulfillmentsModel', {})
    },
    enumerable: true,
    configurable: false,
  },

  // Getter for model of the API data from the shipping charges service, not part of the order
  shipChargesModel: {
    get: function getShipChargesModel() {
      logger('Ship Charges Model', 'debug')()
      return get(this, 'shipChargesModel', {})
    },
    enumerable: true,
    configurable: false,
  },

  // Getter for model of the API data from the saved gift cards service. Not part of the order
  savedGiftCardsModel: {
    get: function getSavedGiftCardsModel() {
      logger('Gift Saved Cards Model', 'debug')()
      return get(this, 'savedGiftCardsModel', {})
    },
    enumerable: true,
    configurable: false,
  },

  getSelectedShipMethodDescription: {
    get: function getSelectedShipMethodDescription() {
      const selectedShip = defaultTo(get(this.model, 'getters.filteredShipLineItems[0].shipMethod.description'), '')
      return TextUtils.toSentenceCase(selectedShip)
    },
    enumerable: true,
    configurable: false,
  },

  getGiftShipMethodDescription: {
    get: function getGiftShipMethodDescription() {
      const selectedShip = defaultTo(get(this.model, 'getters.filteredPhysicalGiftLineItems[0].shipMethod.description'), '')
      return selectedShip ? TextUtils.toSentenceCase(selectedShip) : 'Gift card shipping'
    },
    enumerable: true,
    configurable: false,
  },

  // Assuming that all items are shipping to the same location,
  // there is a chance that an item can be added to the cart and
  // have an unassigned shippingAddress even after the other items
  // have a shippingAddress set. This will iterate over each filtered
  // list and then find the first one with a shippingAddress and return that.
  getSelectedShipAddress: {
    get: function getSelectedShipAddress() {
      const allShipItems = get(this.model, 'getters.filteredShipLineItemsWithGC', [])
      const selected = get(allShipItems.find(item => !isNil(get(item, 'shippingAddress'))), 'shippingAddress')
      return selected
    },
    enumerable: true,
    configurable: false,
  },

  getOrderAddresses: {
    get: function getOrderAddresses() {
      const orderBillingAddress = this.model.safeGet('billingAddress')
      const orderShippingAddress = this.model.safeGet('getters.getOrderShippingAddress', null)

      let shippingScrubReady

      // Make sure the addresses are scrubbed
      const billingScrubReady = AddressUtils.isReadyForScrubbing(get(orderBillingAddress, 'address') || {}) ? orderBillingAddress : {}
      if (isPlainObject(orderShippingAddress)) {
        const addressCopy = Object.assign({}, orderShippingAddress)
        if (!isEmpty(orderBillingAddress)) {
          const billingPhone = get(orderBillingAddress, 'address.phoneNumber')
          const billingEmail = get(orderBillingAddress, 'address.email')

          // apply the billing phone and email to shipping address
          Object.assign(get(addressCopy, 'address'), {
            phoneNumber: billingPhone,
            email: billingEmail,
          })
        }
        shippingScrubReady = AddressUtils.isReadyForScrubbing(get(addressCopy, 'address') || {}) ? addressCopy : {}
      } else {
        shippingScrubReady = orderShippingAddress
      }

      // If there are no usable addresses, exit
      if (isEmpty(orderBillingAddress)
        && isEmpty(orderShippingAddress)) {
        throw new Error('No addresses to apply as a shipping address')
      }

      // Return addresses for further processing
      return {
        billingAddress: billingScrubReady,
        shippingAddress: shippingScrubReady,
      }
    },
    enumerable: true,
    configurable: false,
  },

  determineShippingAddress: {
    get: function determineShippingAddress() {
      const {
        billingAddress = {},
        shippingAddress = {},
      } = this.model.safeGet('getters.getOrderAddresses')

      let finalAddress = null


      if (!isEmpty(shippingAddress)) {
        if (isNumber(shippingAddress)) {
          finalAddress = null
        } else {
          finalAddress = shippingAddress
        }
      } else if (!isEmpty(billingAddress)) {
        finalAddress = billingAddress
      }

      // return the correct address to use
      return finalAddress
    },
    enumerable: true,
    configurable: false,
  },

  getSelectedShipMethod: {
    get: function getSelectedShipMethod() {
      const item = find(get(this.model, 'getters.filteredShipLineItems', []), i => i.shipMethod)
      return item ? item.shipMethod : null
    },
    enumerable: true,
    configurable: false,
  },

  getGiftShipMethod: {
    get: function getGiftShipMethod() {
      const item = find(get(this.model, 'getters.filteredPhysicalGiftLineItems', []), i => i.shipMethod)
      return item ? item.shipMethod : null
    },
    enumerable: true,
    configurable: false,
  },

  getGiftShipAddress: {
    get: function getGiftShipAddress() {
      const selected = get(this.model, 'getters.filteredPhysicalGiftLineItems[0].shippingAddress', undefined)
      return selected
    },
    enumerable: false,
    configurable: false,
  },

  getOrderShippingAddress: {
    get: function getOrderShippingAddress() {
      // Note, at the onset of op-checkout-web, there was/is one shipping address per order
      // In versions yet to come, this method my no longer abide
      // Logic prefers products over gift cards
      const itemsShippingAddress = this.model.safeGet('getters.getSelectedShipAddress', undefined)
      const giftCardsShippingAddress = this.model.safeGet('getters.getGiftShipAddress', undefined)
      return itemsShippingAddress || giftCardsShippingAddress
    },
    enumerable: false,
    configurable: false,
  },

  allShipItemsHaveAddress: {
    get: function allShipItemsHaveAddress() {
      let allItemsHaveAddress = true
      const shipItems = this.model.safeGet('getters.filteredShipLineItemsWithGC', [])

      for (let i = 0, l = shipItems.length; i < l; i += 1) {
        if (isNil(get(shipItems[i], 'shippingAddress'))) {
          allItemsHaveAddress = false
          break
        }
      }

      return allItemsHaveAddress
    },
    enumerable: false,
    configurable: false,
  },

  getDeliveryFees: {
    get: function getDeliveryFees() {
      logger('taxes.fees', 'debug')()
      return get(this.model, 'taxes.fees', [])
    },
    enumerable: true,
  },

  getDividendTender: {
    get: function getDividendTender() {
      const tenders = get(this.model, 'tenders', [])
      return find(tenders, tender => tender.type === 'DIVIDEND')
    },
    enumerable: true,
    configurable: false,
  },

  getPayPalTender: {
    get() {
      const tenders = get(this.model, 'tenders')

      if (tenders && tenders.length) {
        return find(tenders, tender => tender.type === 'PAYPAL' || tender.type === 'PAYPAL_EXPRESS')
      }
      return null
    },
    enumerable: true,
    configurable: false,
  },

  // We want to know if there is an authenticated PP tender. To verify it is
  // authenticated we can check for a payerId.
  hasPayPalTender: {
    get() {
      const tender = this.model.getters.getPayPalTender

      if (tender) {
        return tender.hasPayerId
      }
      return null
    },
    enumerable: true,
    configurable: false,
  },

  // Getter for user object
  userModel: {
    get: function getUserModel() {
      logger('GET User Modal', 'debug')()
      return get(this.model, 'user', {})
    },
    enumerable: false,
    configurable: false,
  },

  customerInfoModel: {
    get: function getCustomerInfo() {
      logger('GET Customer Info', 'debug')()
      return get(this.model, 'customerInfo', {})
    },
    enumerable: false,
    configurable: false,
  },

  userSavedCreditCards: {
    get: function getUserSavedCreditCards() {
      logger('GET User Modal', 'debug')()
      const savedTenders = get(this.model, 'user.savedTenders', [])
      return (Array.isArray(savedTenders))
        ? savedTenders.filter(tender => tender.type && tender.type === 'CREDITCARD_REPOSITORY')
        : []
    },
    enumerable: true,
    configurable: false,
  },

  tendersModel: {
    get: function getTendersModel() {
      logger('Tenders Model', 'debug')()
      return get(this, 'tendersModel', {})
    },
    enumerable: true,
    configurable: false,
  },

  // Creates a new array that updates saved credit cards based on sequenceId
  // This way, when a card is editted, we have one source of truth for the users credit cards
  filteredCreditCards: {
    get: function getFilteredCreditCards() {
      let savedTenders = this.model.getters.userSavedCreditCards
      let storedTender = this.model.getters.tendersModel.storedCreditCard

      if (!!storedTender && storedTender.updateSavedTender && !storedTender.isNew) {
        let index = savedTenders.findIndex(x => x.sequenceId === storedTender.sequenceId)
        savedTenders[index].expiration = storedTender.expiration
        savedTenders[index].pin = storedTender.pin
      }

      return savedTenders
    },
    enumerable: true,
    configurable: false,
  },

  // Get valid saved addresses on the user object. This
  // will ensure that the addresses are both present
  // and able to be applied to the order. Some of the
  // addresses that can be saved to the user account are
  // technically invalid. This will only return valid
  // addresses
  validSavedAddresses: {
    get: function getValidSavedAddresses() {
      const saved = this.model.safeGet('user.savedShippingAddresses')
      return ((saved !== undefined) && (saved !== null) && Array.isArray(saved))
        ? saved.filter(a => AddressUtils.isReadyForScrubbing(a.address))
        : []
    },
  },

  getMerchandiseShipCharges: {
    get: function getMerchandiseShipCharges() {
      const group = find(this.model.safeGet('shippingChargesGroups', []), {
        shippingGroupCategory: 'MERCHANDISE',
      })
      return group ? group.shippingChargeAdjusted : undefined
    },
    enumerable: false,
    configurable: false,
  },

  getGiftCardShipCharges: {
    get: function getGiftCardShipCharges() {
      const group = find(this.model.safeGet('shippingChargesGroups', []), {
        shippingGroupCategory: 'GIFT_CARD',
      })
      return group ? group.shippingChargeAdjusted : undefined
    },
    enumerable: false,
    configurable: false,
  },

  isReadyForShipping: {
    get: function isReadyForShipping() {
      let isMethodReady = false
      let isAddressReady = false
      const items = get(this.model, 'getters.filteredShipLineItemsWithGC', [])

      isMethodReady = items.every(i => !isEmpty(get(i, 'shipMethod', null)))
      isAddressReady = items.every((i) => {
        const itemAddress = get(i, 'shippingAddress.address')

        if (isPlainObject(itemAddress)) {
          return AddressUtils.isScrubbed(itemAddress)
        }
        return false
      })

      return isMethodReady && isAddressReady
    },
    enumerable: false,
    configurable: false,
  },

  // Checks to see if there are multiple shipping addresses
  // (not including stores) on the order so that the user
  // can make a decision about which address they want to use.
  getItemsWithUniqShipToAddresses: {
    get: function getItemsWithUniqShipToAddresses() {
      const items = get(this.model, 'getters.filteredShipLineItems', [])

      // Get items with addresses first
      // omitting items without addresses.
      const itemsWithAddresses = items.filter(item => ItemUtils.hasShippingAddress(item))

      const ItemsWithUniqAddresses = uniqWith(itemsWithAddresses, (arrVal, othVal) => {
        return AddressUtils.isMatchIgnoreCase(arrVal.shippingAddress, othVal.shippingAddress)
      })

      return ItemsWithUniqAddresses
    },
    enumerable: false,
    configurable: false,
  },

  hasMultipleBillingAddresses: {
    get: function hasMultipleBillingAddresses() {
      const billingAddress = get(this.model, 'billingAddress')
      const savedBillingAddress = get(this.model, 'user.savedBillingAddress')

      if (billingAddress && billingAddress.address
        && savedBillingAddress && savedBillingAddress.address
        && AddressUtils.isReadyForScrubbing(billingAddress.address)
        && AddressUtils.isReadyForScrubbing(savedBillingAddress.address)) {
        return !AddressUtils.isMatchIgnoreCase(billingAddress, savedBillingAddress, ['addressType'])
      }

      return false
    },
    enumerable: false,
    configurable: false,
  },

  hasMultipleShipToAddresses: {
    get: function hasMultipleShipToAddresses() {
      return get(this.model, 'getters.getItemsWithUniqShipToAddresses', []).length > 1
    },
    enumerable: false,
    configurable: false,
  },

  getUnselectedSavedShippingAddresses: {
    get: function getUnselectedSavedShippingAddresses() {
      let addresses = []
      const saved = this.model.safeGet('getters.validSavedAddresses', [])
      const billing = this.model.safeGet('billingAddress')
      const selected = this.model.safeGet('getters.getOrderShippingAddress')

      if (billing) addresses.unshift(billing)
      addresses = concat(addresses, saved)

      return filter(addresses, (address) => {
        if (!address) return false

        return !AddressUtils.isMatchIgnoreCase(address, selected)
      })
    },
    enumerable: false,
    configurable: false,
  },

  getGiftMessages: {
    get: function getGiftMessages() {
      const notes = get(this.model, 'notes') || []
      return notes.filter(message => message.type === 'GIFT_NOTE')
    },
    enumerable: false,
    configurable: false,
  },

  hasGiftOptions: {
    get: function hasGiftOptions() {
      return get(this.model, 'getters.getGiftMessages', []).length > 0 || get(this.model, 'isGift')
    },
    enumerable: false,
    configurable: false,
  },

  hasGiftMessage: {
    get: function hasGiftMessage() {
      return get(this.model, 'getters.getGiftMessages', []).length > 0
    },
    enumerable: false,
    configurable: false,
  },

  hasGiftReceipt: {
    get: function hasGiftReceipt() {
      return get(this.model, 'isGift')
    },
    enumerable: false,
    configurable: false,
  },

  isBillingShipping: {
    get: function isBillingShipping() {
      const billingAddress = get(this.model, 'billingAddress')
      const shippingAddress = get(this.model, 'getters.getOrderShippingAddress')

      return AddressUtils.isMatchIgnoreCase(billingAddress, shippingAddress)
    },
    enumerable: false,
    configurable: false,
  },

  getLastEGiftScheduledDeliveryDate: {
    get: function getLastEGiftScheduledDeliveryDate() {
      const scheduledDeliveryDates = map(
        get(this.model, 'getters.filteredDigitalGiftLineItems', []),
        item => item.scheduledDeliveryDate
      ).sort()

      return scheduledDeliveryDates.length ?
        scheduledDeliveryDates[scheduledDeliveryDates.length - 1] : null
    },
    enumerable: false,
    configurable: false,
  },

  hasMemberOnlyItems: {
    get: function hasMemberOnlyItems() {
      const lineItems = this.model.safeGet('lineItems', [])
      return filter(lineItems, item => find(item.badges, val => val.title === 'Just for Members'))
    },
    enumerable: false,
    configurable: false,
  },

  /**
   * Gets a Sku level shipping restriction
   * @param  {Object} item    order item
   * @param  {Object} restrictionName    name of the restriction to match on
   * @return {Object}          Restriction object containing name and message
   */
  getSkuItemRestriction: {
    value: function getSkuItemRestriction(item, restrictionName) {
      const skurestrictions = get(item, 'restrictions') || []
      if (skurestrictions.length > 0) {
        return !!find(skurestrictions, restriction => restriction.name === restrictionName)
      }
      return undefined
    },
    enumerable: false,
    configurable: false,
  },

  getSkuItemRestrictionMsg: {
    value: function getSkuItemRestrictionMsg(item, restrictionName) {
      const skurestrictions = get(item, 'restrictions') || []
      if (skurestrictions.length > 0) {
        const restrictionMatch = find(skurestrictions, restriction => restriction.name === restrictionName) || {}
        return restrictionMatch.message
      }
      return ''
    },
    enumerable: false,
    configurable: false,
  },

  hasSkuRestriction: {
    value: function hasSkuRestriction(item) {
      // Specifically for items with SKU level restrictions. These differ from style level restrictions which are pulled
      // from the fulfillments data
      const getters = this.model.safeGet('getters') || {}
      return getters.isUnavailableExpedited(item) || getters.isUnavailableInternational(item) || getters.isUnavailableShipToStore(item)
    },
    enumerable: false,
    configurable: false,
  },

  isUnavailableExpedited: {
    value: function isUnavailableExpedited(item) {
      return this.safeGet('getters.getSkuItemRestriction')(item, SKU_SHIPPING_RESTRICTIONS.EXPEDITED_SHIPPING_RESTRICTION)
    },
    enumerable: false,
    configurable: false,
  },

  isUnavailableInternational: {
    value: function isUnavailableInternational(item) {
      return this.safeGet('getters.getSkuItemRestriction')(item, SKU_SHIPPING_RESTRICTIONS.INTERNATIONAL_SHIPPING_RESTRICTION)
    },
    enumerable: false,
    configurable: false,
  },

  isUnavailableShipToStore: {
    value: function isUnavailableShipToStore(item) {
      return this.safeGet('getters.getSkuItemRestriction')(item, SKU_SHIPPING_RESTRICTIONS.STORE_PICKUP_RESTRICTION)
    },
    enumerable: false,
    configurable: false,
  },

  getUnavailableExpeditedMsg: {
    get: () => 'Faster and Fastest shipping options are not available for this order.',
    enumerable: false,
    configurable: false,
  },

  getUnavailableInternationalMsg: {
    get: () => 'This item can only be shipped to U.S. addresses. To continue with this order, you must update your shipping address. For further support call <a href="tel:1-800-426-4840">1-800-426-4840</a>.',
    enumerable: false,
    configurable: false,
  },

  getUnavailableShipToStoreMsg: {
    get: () => 'This item is not available for in store pickup.',
    enumerable: false,
    configurable: false,
  },

  hasOversizedItems: {
    get: function hasOversizedItems() {
      return !!this.model.safeGet('getters.filteredShipLineItems', [])
        .filter(p => p.hazardous || (p.oversized && p.shippingTier === 5)).length > 0
    },
    enumerable: false,
    configurable: false,
  },

  getSkuRestrictedItems: {
    get: function getSkuRestrictedItems() {
      return this.model.safeGet('getters.filteredShipLineItems', [])
        .filter(p => p.restrictions && p.restrictions.length > 0)
    },
    enumerable: false,
    configurable: false,
  },

  preferredStoreId: {
    get: function preferredStoreId() {
      if (!appProps.get('enable.mystore.chip')) return 0
      if (!this.model.safeGet('user.isGuest')) return this.model.safeGet('user.userPreferences.preferredStore', 0)

      return parseInt(cookie.get('myStoreId') || 0, 10)
    },
    enumerable: false,
    configurable: false,
  },

  totalSavings: {
    get: function totalSavings() {
      return this.model.safeGet('getters.totalCouponDiscounts') + this.model.safeGet('getters.totalItemDiscounts')
    },
    enumerable: false,
    configurable: false,
  },

  totalItemDiscounts: {
    get: function totalItemDiscounts() {
      let savings = 0

      this.model.safeGet('lineItems', []).forEach((item) => {
        savings += (item.price.original - item.price.unit) * item.quantity
      })

      return savings
    },
    enumerable: false,
    configurable: false,
  },

  totalCouponDiscounts: {
    get: function totalCouponDiscounts() {
      const coupons = []

      CouponUtils.getAppliedDiscounts(this.model).forEach((coupon) => {
        let total = 0
        const label = coupon.type === 'AUTO' ? CouponUtils.getAutoCouponLabel(coupon) : `${coupon.name}`

        coupon.couponEffects.forEach((effect) => {
          total += effect.priceDiscount
        })

        coupons.push({
          label,
          total,
        })
      })

      // eslint-disable-next-line no-param-reassign, no-return-assign
      const totalCouponDiscount = coupons.reduce((acc, cur) => acc += cur.total, 0)

      return totalCouponDiscount
    },
    enumerable: false,
    configurable: false,
  },

  getBYRestrictionMessage: {
    get: function getBYRestrictionMessage() {
      const orderIssues = get(this.model, 'orderIssues', [])
      if (orderIssues.length) {
        const errors = new OrderIssues(orderIssues);
        const noLocationRestriction = errors.hasNoLocationBySRPBlueYonderRestriction();
        const byZipCodeRestriction = errors.hasEADConfigBlueYonderRestriction();
        const storeConfigRestriction = errors.hasBlueYonderStoreConfigRestriction();

        for (const error of errors.issues) {
          if (noLocationRestriction) {
            return this.model.getters.noLocationsBYSRPErrorMessage(error);
          }
          if (byZipCodeRestriction) {
            return 'byZipCodeRestriction';
          }
          if(storeConfigRestriction){
            return 'storeConfigRestriction';
          }
        }
      }
      return null;
    },
    enumerable: true,
  },

  noLocationsBYSRPErrorMessage: {
    value: function noLocationsBYSRPErrorMessage ( orderIssue ) {
      const error = new OrderIssues(orderIssue);
      const token = error.extractBYErrorDescription(orderIssue.message)
      const defaultMessage = 'This item cannot ship to this shipping address.';
      if (token === null) {
        return defaultMessage;
      }
      if (token === 'SRP_NON_US') {
        return 'Item(s) in your cart cannot ship to this international address';
      }
      if (token === 'SRP_PO_BOX_ADDRESS' || token === 'SRP_US_PR_APO_POBOX') {
        return 'byPoBoxRestriction';
      }
      return defaultMessage;
    },
    enumerable: true,
  },
}
